import { queryKeyStore } from '../queryKeyStore'
import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useContext } from 'react'
import { useEffect, useSelector } from 'src/hooks'
import useUpdateCheckoutVouchers from 'src/hooks/useUpdateCheckoutVouchers'
import { CheckoutContext } from 'src/saleor/context/CheckoutContext'
import {
  base64URLEncode,
  generateCodeVerifier,
  getPersistedSaleorAuth,
  persistSaleorAccessToken,
  sha256,
} from 'src/saleor/utils/auth'
import {
  FontFragment,
  GetAlertsQueryVariables,
  GetCampaignOrdersQueryVariables,
  GetCampaignQueryVariables,
  GetContactRequestByTokenQueryVariables,
  GetDigitalCardByTokenQueryVariables,
  GetDraftedCardsQueryVariables,
  GetImagesQueryVariables,
  GetItemQueryVariables,
  GetItemsQueryVariables,
  GetLayoutQueryVariables,
  GetLayoutsQueryVariables,
  GetNotificationsQueryVariables,
  GetOrderQueryVariables,
  GetPaginatedRecipientsQueryVariables,
  GetPlanMoveQueryVariables,
  GetRemindersQueryVariables,
  GetSendableCardQueryVariables,
  GetSentCardsCountQueryVariables,
  GetSentCardsQueryVariables,
  GetSharedCampaignsQueryVariables,
  GetSharedContactsQueryVariables,
  GetSponsorQueryVariables,
  GetUsersQueryVariables,
  StickerFilters,
} from 'src/graphql/generated/graphql'
import * as graphql from 'src/graphql/generated/graphql'
import {
  AttachCustomerMutationVariables,
  CampaignStoreSubcategoryQueryVariables,
  CheckoutFragment,
  CheckoutQueryVariables,
  CompleteCheckoutMutationVariables,
  DeleteCheckoutLinesMutationVariables,
  InitializeTransactionMutationVariables,
  ProductVariantQueryVariables,
  ProductVariantsQueryVariables,
  SaleorOrderQueryVariables,
  UpdateCheckoutBillingAddressMutationVariables,
  UpdateCheckoutEmailMutationVariables,
  UpdateCheckoutLineQuantityMutationVariables,
} from 'src/saleor_graphql/generated/graphql'
import {
  addCheckoutLines,
  addCheckoutPromoCode,
  attachCustomerToCheckout,
  authorizeSaleorOIDC,
  completeCheckout,
  createCheckout,
  createCheckoutPayment,
  createTransaction,
  deleteCheckoutLines,
  getProductVariants,
  getSaleorAccessToken,
  getSaleorRefreshToken,
  removeCheckoutPromoCode,
  updateCheckoutBillingAddress,
  updateCheckoutEmail,
  updateCheckoutLineQuantity,
  updateSaleorCustomer,
} from './fetching'
import { connectionFactory } from 'src/react_query/utils/cursorPagination'
import { useOffsetLimitPagination } from 'src/hooks/useGetOffsetLimitPagination'
import { browserHistory } from 'src/redux/browserHistory'
import { AppLabel } from 'src/helpers/userApplicationSettings/supportedApplicationSettings'

type MakeOptional<T, K extends keyof T> = Partial<Pick<T, K>> & Omit<T, K>

// Stale time for data that does not change frequently (e.g. marketing paragraphs, catalog)
export const stableDataStaleTime = 600_000

export const useAccountQuery = ({
  enabled,
  suspense,
}: { enabled?: boolean; suspense?: boolean } = {}) => {
  const res = useQuery({
    ...queryKeyStore.account.detail,
    enabled,
    suspense,
    staleTime: 15_000,
    keepPreviousData: true,
  })

  return res
}

export const useUpsells = ({
  enabled,
  suspense,
}: { enabled?: boolean; suspense?: boolean } = {}) => {
  const res = useQuery({
    ...queryKeyStore.upsell.list,
    enabled,
    suspense,
    staleTime: stableDataStaleTime,
  })

  return res
}

export const useAddPaymentSettingsDailyCap = ({
  enabled,
  suspense,
}: { enabled?: boolean; suspense?: boolean } = {}) => {
  const res = useQuery({
    ...queryKeyStore.daily_cap.add_payment_settings,
    enabled,
    suspense,
  })

  return res
}

export const useAffiliateCountries = () => {
  const res = useQuery(queryKeyStore.affiliate_countries.list)

  return {
    ...res,
    data: res.data?.affiliateCountries,
  }
}

export const useAlerts = (
  resultsPerPage: number = 10,
  variables: Omit<GetAlertsQueryVariables, 'offset' | 'limit'> = {},
) => {
  return useInfiniteQuery({
    queryKey: queryKeyStore.alerts.infinite({
      ...variables,
      offset: 0,
      limit: resultsPerPage,
    }).queryKey,
    queryFn: args => {
      const { pageParam = 0 } = args

      return queryKeyStore.alerts
        .infinite({ ...variables, offset: pageParam, limit: resultsPerPage })
        .queryFn(args)
    },
    getNextPageParam: (lastPage, allPages) => {
      const hasMore =
        lastPage.paginatedCampaignShares.hasMore ||
        lastPage.paginatedContactShares.hasMore ||
        lastPage.paginatedNotifications.hasMore ||
        lastPage.paginatedReminders.hasMore

      const totalCampaignSharesFetched = allPages.reduce(
        (prev, curr) => prev + curr.paginatedCampaignShares.results.length,
        0,
      )
      const totalContactSharesFetched = allPages.reduce(
        (prev, curr) => prev + curr.paginatedContactShares.results.length,
        0,
      )
      const totalNotificationsFetched = allPages.reduce(
        (prev, curr) => prev + curr.paginatedNotifications.results.length,
        0,
      )
      const totalRemindersFetched = allPages.reduce(
        (prev, curr) => prev + curr.paginatedReminders.results.length,
        0,
      )

      const maxFetched = Math.max(
        totalCampaignSharesFetched,
        totalNotificationsFetched,
        totalRemindersFetched,
        totalContactSharesFetched,
      )

      if (!hasMore) {
        return undefined
      }

      return maxFetched
    },
  })
}

export const useAvailableDailyHeartfelt = ({
  enabled,
  suspense,
}: { enabled?: boolean; suspense?: boolean } = {}) => {
  const res = useQuery({
    ...queryKeyStore.daily_cap.heartfelt,
    enabled,
    suspense,
  })

  return res
}

export const useAvailableUnlimitedDailyHeartfelt = ({
  enabled,
  suspense,
}: { enabled?: boolean; suspense?: boolean } = {}) => {
  const res = useQuery({
    ...queryKeyStore.daily_cap.unlimited_heartfelt,
    enabled,
    suspense,
  })

  return res
}

export const useByDesignLoginLink = () =>
  useQuery({
    ...queryKeyStore.bydesign_login_link.detail,
    staleTime: 60_000, // 1 minute
  })

export const useShouldVerifyEmail = () =>
  useQuery({
    ...queryKeyStore.should_verify_email.detail,
    staleTime: 60_000, // 1 minute
  })

export const useDetailedCountries = ({
  enabled,
  suspense,
}: { enabled?: boolean; suspense?: boolean } = {}) =>
  useQuery({
    ...queryKeyStore.countries.list,
    staleTime: stableDataStaleTime,
    enabled,
    suspense,
  })

export const usePhoneNumberCountries = ({
  enabled,
  suspense,
}: { enabled?: boolean; suspense?: boolean } = {}) =>
  useQuery({
    ...queryKeyStore.phone_number_countries.list,
    staleTime: stableDataStaleTime,
    enabled,
    suspense,
  })

export const usePlanMove = (planIdFrom: number, planIdTo: number) => {
  return useQuery(queryKeyStore.plan_move.detail({ planIdFrom, planIdTo }))
}

export const usePromptingsCoachDiscounts = () =>
  useQuery(queryKeyStore.promptings_coach_discounts.detail)

export const usePromptingsEventDates = () =>
  useQuery({
    ...queryKeyStore.marketing_content.promptings_event_dates,
    staleTime: stableDataStaleTime,
  })

export const useAccountCreatedContents = () => {
  return useQuery({
    ...queryKeyStore.marketing_content.account_created,
    staleTime: stableDataStaleTime,
  })
}

export const useNotifications = (variables: GetNotificationsQueryVariables) => {
  return useQuery(queryKeyStore.notification.list(variables))
}

export const useNotificationCount = () =>
  useQuery(queryKeyStore.notification.count)

export const useReceivedPendingMembershipInvites = () => {
  return useQuery(queryKeyStore.received_pending_membership_invites.detail)
}

export const useReminders = (variables: GetRemindersQueryVariables) => {
  return useQuery(queryKeyStore.reminders.detail(variables))
}

export const useSharedCampaigns = (
  variables: GetSharedCampaignsQueryVariables,
) => {
  return useQuery(queryKeyStore.shared_campaigns.detail(variables))
}

export const useSharedContacts = (
  variables: GetSharedContactsQueryVariables,
) => {
  return useQuery(queryKeyStore.shared_contacts.detail(variables))
}

export const useContactRequests = () => {
  return useQuery(queryKeyStore.contact_requests.list)
}

export const useContactRequestByToken = (
  variables: GetContactRequestByTokenQueryVariables,
  { enabled, suspense }: { enabled?: boolean; suspense?: boolean } = {},
) => {
  return useQuery({
    ...queryKeyStore.contact_requests.by_token(variables),
    enabled,
    suspense,
  })
}

export const useGetEarnedBadges = () => {
  return useQuery(queryKeyStore.coaching_badges.list({ hasEarned: true }))
}

export const useGetInProgressBadges = () => {
  const query = useQuery(
    queryKeyStore.coaching_badges.list({ hasEarned: false }),
  )
  return query
}

// This should not every pass empty string as we have this query marked as dependent with the enabled flag
// See https://tanstack.com/query/latest/docs/react/guides/dependent-queries
export const useGetCard = (
  variables: Partial<graphql.GetCardQueryVariables>,
  { suspense }: { suspense?: boolean } = {},
) => {
  return useQuery({
    ...queryKeyStore.card.detail({ id: variables.id ?? '' }),
    enabled: !!variables.id,
    suspense,
    staleTime: 10_000,
    keepPreviousData: true,
  })
}

export const useGetCardCatalog = ({
  suspense,
}: { suspense?: boolean } = {}) => {
  return useQuery({
    ...queryKeyStore.cardCatalog.detail,
    suspense,
    staleTime: stableDataStaleTime,
  })
}

export const useGetCardCategories = ({
  enabled,
  suspense,
}: { enabled?: boolean; suspense?: boolean } = {}) => {
  return useQuery({
    ...queryKeyStore.cardCategory.list,
    enabled,
    suspense,
    staleTime: stableDataStaleTime,
  })
}

export const useGetCardCategory = (
  variables: Partial<graphql.GetCardCategoryQueryVariables>,
  { suspense }: { suspense?: boolean } = {},
) => {
  return useQuery({
    ...queryKeyStore.cardCategory.detail({ id: variables.id ?? '' }),
    enabled: !!variables.id,
    suspense,
  })
}

export const useGetCustomCards = ({ enabled }: { enabled?: boolean } = {}) => {
  const { getNextPageParam, getOffset, limit } = useOffsetLimitPagination(20)

  return useInfiniteQuery({
    ...queryKeyStore.customCard.infinite({ limit: limit }),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length === 0) {
        // We'll stop when we receive an empty page
        return undefined
      } else {
        const pageNumber = getNextPageParam(
          // As we do not have the total count, we'll just stop when we receive an empty page
          // in the condition above. We can just pretend there's an infinite number of layouts here.
          { results: lastPage, count: Infinity },
          allPages.map(page => ({
            results: page,
            count: page.length ?? 0,
          })),
        )
        return pageNumber !== undefined ? getOffset(pageNumber) : undefined
      }
    },
    enabled,
  })
}

export const useGetDigitalCardByToken = (
  variables: GetDigitalCardByTokenQueryVariables,
  { suspense }: { suspense?: boolean } = {},
) => {
  return useQuery({
    ...queryKeyStore.digitalCard.byToken({ token: variables.token }),
    enabled: !!variables.token,
    suspense,
  })
}

export const useGetLegacyPicturePlusCards = ({
  enabled,
}: { enabled?: boolean } = {}) => {
  const { getNextPageParam, getOffset, limit } = useOffsetLimitPagination(20)

  return useInfiniteQuery({
    ...queryKeyStore.legacyPicturePlusCard.infinite({ limit: limit }),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length === 0) {
        // We'll stop when we receive an empty page
        return undefined
      } else {
        const pageNumber = getNextPageParam(
          // As we do not have the total count, we'll just stop when we receive an empty page
          // in the condition above. We can just pretend there's an infinite number of layout here.
          { results: lastPage, count: Infinity },
          allPages.map(page => ({
            results: page,
            count: page.length ?? 0,
          })),
        )
        return pageNumber !== undefined ? getOffset(pageNumber) : undefined
      }
    },
    enabled,
  })
}

export const useGetSendableCard = (
  variables: Partial<GetSendableCardQueryVariables>,
  { suspense }: { suspense?: boolean } = {},
) => {
  return useQuery({
    ...queryKeyStore.sendableCard.detail({ id: variables.id ?? '' }),
    enabled: !!variables.id,
    suspense,
    staleTime: stableDataStaleTime,
  })
}

export const useGetSendableCards = (
  variables: Omit<graphql.GetSendableCardsQueryVariables, 'offset' | 'limit'>,
  { enabled }: { enabled?: boolean } = {},
) => {
  const { getNextPageParam, getOffset, limit } = useOffsetLimitPagination(20)

  return useInfiniteQuery({
    ...queryKeyStore.sendableCard.infinite({ ...variables, limit: limit }),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length === 0) {
        // We'll stop when we receive an empty page
        return undefined
      } else {
        const pageNumber = getNextPageParam(
          // As we do not have the total count, we'll just stop when we receive an empty page
          // in the condition above. We can just pretend there's an infinite number of layout here.
          { results: lastPage, count: Infinity },
          allPages.map(page => ({
            results: page,
            count: page.length ?? 0,
          })),
        )
        return pageNumber !== undefined ? getOffset(pageNumber) : undefined
      }
    },
    enabled,
  })
}

export const useDesignColorsBySendableCardId = (
  variables: MakeOptional<
    graphql.GetDesignColorsBySendableCardIdQueryVariables,
    'id'
  >,
  { enabled, suspense }: { enabled?: boolean; suspense?: boolean } = {},
) => {
  return useQuery({
    ...queryKeyStore.sendableCard.designColorsById({
      ...variables,
      id: variables.id ?? '',
    }),
    enabled: !!variables.id && enabled,
    suspense,
    staleTime: stableDataStaleTime,
  })
}

export const useGetItemCategories = ({ suspense }: { suspense?: boolean }) =>
  useQuery({ ...queryKeyStore.itemCategory.list, suspense })

export const useGetItemCategory = (
  variables: graphql.GetItemCategoryQueryVariables,
  { enabled, suspense }: { enabled?: boolean; suspense?: boolean } = {},
) =>
  useQuery({
    ...queryKeyStore.itemCategory.detail(variables),
    enabled: !!variables.id && enabled,
    suspense,
    staleTime: stableDataStaleTime,
  })

export const useItem = (variables: GetItemQueryVariables) =>
  useQuery({ ...queryKeyStore.item.detail(variables), suspense: true }) // TODO - Change this to no longer suspend when we change Campaign View UI

export const useItems = (variables: GetItemsQueryVariables) =>
  useQuery({ ...queryKeyStore.item.list(variables) })

export const useLeaderBoards = () => useQuery(queryKeyStore.leader_board.list)

export const useCampaign = (variables: GetCampaignQueryVariables) =>
  useQuery({ ...queryKeyStore.campaign.detail(variables) })

export const usePaginatedCampaigns = (
  variables: Omit<
    graphql.GetPaginatedCampaignsQueryVariables,
    'offset' | 'limit'
  > & {
    page: number
    resultsPerPage?: number
  },
  { enabled, suspense }: { enabled?: boolean; suspense?: boolean } = {},
) => {
  const { page, ...vars } = variables
  const resultsPerPage = vars.resultsPerPage || 20
  const { getOffset, limit } = useOffsetLimitPagination(resultsPerPage)
  const offset = getOffset(page)
  return useQuery({
    ...queryKeyStore.campaign.listPaginated({ ...vars, offset, limit }),
    enabled,
    suspense,
  })
}

export const usePaginatedSentCards = (
  variables: Omit<GetSentCardsQueryVariables, 'offset' | 'limit'> & {
    page: number
  },
  { enabled, suspense }: { enabled?: boolean; suspense?: boolean } = {},
) => {
  const { page, ...vars } = variables
  const resultsPerPage = 20
  const { getOffset, limit } = useOffsetLimitPagination(resultsPerPage)
  const offset = getOffset(page)
  return useQuery({
    ...queryKeyStore.sent_card.list_paginated({ offset, limit, ...vars }),
    enabled,
    suspense,
  })
}

export const usePaginatedDraftedOrders = (
  page: number,
  { enabled, suspense }: { enabled?: boolean; suspense?: boolean } = {},
) => {
  const resultsPerPage = 20
  const { getOffset, limit } = useOffsetLimitPagination(resultsPerPage)
  const offset = getOffset(page)
  return useQuery({
    ...queryKeyStore.order.drafted({ offset, limit }),
    enabled,
    suspense,
  })
}

export const useSentCardsCount = (
  variables: GetSentCardsCountQueryVariables,
  {
    enabled,
  }: {
    enabled?: boolean
  } = {},
) => useQuery({ ...queryKeyStore.sent_card.count(variables), enabled })

export const useDraftedCards = (variables: GetDraftedCardsQueryVariables) =>
  useQuery({
    ...queryKeyStore.draftedCard.list(variables),
  })

export const useInfiniteDraftedCards = () => {
  const { getNextPageParam, getOffset, limit } = useOffsetLimitPagination(20)

  return useInfiniteQuery({
    ...queryKeyStore.draftedCard.infinite({ limit: limit }),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length === 0) {
        // We'll stop when we receive an empty page
        return undefined
      } else {
        const pageNumber = getNextPageParam(
          // As we do not have the total count, we'll just stop when we receive an empty page
          // in the condition above. We can just pretend there's an infinite number of drafted cards here.
          { results: lastPage, count: Infinity },
          allPages.map(page => ({
            results: page,
            count: page.length ?? 0,
          })),
        )
        return pageNumber !== undefined ? getOffset(pageNumber) : undefined
      }
    },
  })
}

export const useCampaignOrders = (
  variables: Omit<GetCampaignOrdersQueryVariables, 'offset' | 'limit'>,
) => {
  const resultsPerPage = 3
  const { getNextPageParam, getOffset, limit } =
    useOffsetLimitPagination(resultsPerPage)

  return useInfiniteQuery({
    queryKey: queryKeyStore.infinite_campaign_order.list({
      id: variables.id,
      offset: 0,
      limit,
    }).queryKey,
    queryFn: args => {
      const { pageParam = 1 } = args
      const offset = getOffset(pageParam)

      return queryKeyStore.infinite_campaign_order
        .list({
          id: variables.id,
          offset,
          limit,
        })
        .queryFn(args)
    },
    getNextPageParam,
  })
}

export const usePaginatedInvites = ({ page }: { page: number }) => {
  const resultsPerPage = 20
  const { getOffset, limit } = useOffsetLimitPagination(resultsPerPage)
  const offset = getOffset(page)
  return useQuery({
    ...queryKeyStore.paginated_invites.list({ offset, limit }),
  })
}

export const useInfiniteInvites = () => {
  const resultsPerPage = 20
  const { getNextPageParam, getOffset, limit } =
    useOffsetLimitPagination(resultsPerPage)

  return useInfiniteQuery({
    queryKey: queryKeyStore.paginated_invites.infinite({
      offset: 0,
      limit,
    }).queryKey,
    queryFn: args => {
      const { pageParam = 1 } = args
      const offset = getOffset(pageParam)

      return queryKeyStore.paginated_invites
        .infinite({
          offset,
          limit,
        })
        .queryFn(args)
    },
    getNextPageParam,
  })
}

// TODO we should refactor this to fetch single pieces/pages of marketing content instead of everything at once
// yes we should... Query is too long to hit the CDN cache
export const useMarketingContent = ({
  enabled,
  suspense,
}: { enabled?: boolean; suspense?: boolean } = {}) => {
  return useQuery({
    ...queryKeyStore.marketing_content.list,
    staleTime: stableDataStaleTime,
    enabled,
    suspense,
  })
}

export const useFairUseMarketingContent = ({
  enabled,
  suspense,
}: { enabled?: boolean; suspense?: boolean } = {}) => {
  return useQuery({
    ...queryKeyStore.marketing_content.fair_use,
    staleTime: stableDataStaleTime,
    enabled,
    suspense,
  })
}

export const useAffiliateUpsaleMarketingContent = ({
  enabled,
  suspense,
}: { enabled?: boolean; suspense?: boolean } = {}) => {
  return useQuery({
    ...queryKeyStore.marketing_content.affiliate_upsale,
    staleTime: stableDataStaleTime,
    enabled,
    suspense,
  })
}

export const useAffiliateUpsaleDialogMarketingContent = ({
  enabled,
  suspense,
}: { enabled?: boolean; suspense?: boolean } = {}) => {
  return useQuery({
    ...queryKeyStore.marketing_content.affiliate_upsale_dialog,
    staleTime: stableDataStaleTime,
    enabled,
    suspense,
  })
}

export const useMarketingParagraphById = (
  variables: { id: string },
  { enabled, suspense }: { enabled?: boolean; suspense?: boolean } = {},
) => {
  return useQuery({
    ...queryKeyStore.marketing_content.paragraph_detail(variables),
    staleTime: stableDataStaleTime,
    enabled,
    suspense,
  })
}

export const useFeaturedCards = ({
  enabled,
  suspense,
}: { enabled?: boolean; suspense?: boolean } = {}) => {
  return useQuery({
    ...queryKeyStore.featured_cards.list,
    staleTime: stableDataStaleTime,
    enabled,
    suspense,
  })
}

export const useCollection = (
  variables: { id: string },
  { enabled, suspense }: { enabled?: boolean; suspense?: boolean } = {},
) => {
  return useQuery({
    ...queryKeyStore.collection.detail(variables),
    staleTime: stableDataStaleTime,
    enabled,
    suspense,
  })
}

export const useGetActRecipientActionScreenContent = () => {
  return useQuery({
    ...queryKeyStore.marketing_content.act_recipient_action_screens,
    staleTime: stableDataStaleTime,
  })
}

export const useLeaderBoardMarketingContent = () => {
  return useQuery({
    ...queryKeyStore.marketing_content.leader_board,
    staleTime: stableDataStaleTime,
  })
}

export const useMarketingBanners = () => {
  return useQuery({
    ...queryKeyStore.marketing_banner.list,
    staleTime: stableDataStaleTime,
  })
}

export const useFlags = ({
  enabled,
  suspense,
}: { enabled?: boolean; suspense?: boolean } = {}) => {
  const query = useQuery({
    ...queryKeyStore.flags.list,
    enabled,
    suspense,
    staleTime: stableDataStaleTime,
  })
  return {
    ...query,
    data: query.data?.flags,
  }
}

export const useProductionInfo = (
  variables: graphql.GetProductionInfoQueryVariables,
  { enabled, suspense }: { enabled?: boolean; suspense?: boolean } = {},
) => {
  const query = useQuery({
    ...queryKeyStore.production_info.detail(variables),
    enabled,
    suspense,
  })

  return {
    ...query,
    data: query.data?.productionInfo,
  }
}

export const usePaginatedRecipients = (
  variables: Omit<GetPaginatedRecipientsQueryVariables, 'offset' | 'limit'> & {
    page: number
  },
  { enabled, suspense }: { enabled?: boolean; suspense?: boolean } = {},
) => {
  const { page, ...vars } = variables
  const resultsPerPage = 20
  const { getOffset, limit } = useOffsetLimitPagination(resultsPerPage)
  const offset = getOffset(page)
  const query = useQuery({
    ...queryKeyStore.recipients.list({ offset, limit, ...vars }),
    enabled,
    suspense,
  })

  return {
    ...query,
    data: query.data?.paginatedRecipients,
  }
}

export const useLayouts = (
  variables: GetLayoutsQueryVariables,
  { enabled, suspense }: { enabled?: boolean; suspense?: boolean } = {},
) => {
  const query = useQuery({
    ...queryKeyStore.layout.list(variables),
    enabled,
    suspense,
    staleTime: stableDataStaleTime,
  })
  return {
    ...query,
    data: query.data?.layouts,
  }
}

export const useGetDigtalCardOrder = (
  variables: graphql.GetDigitalCardQueryVariables,
  { enabled, suspense }: { enabled?: boolean; suspense?: boolean } = {},
) => {
  return useQuery({
    ...queryKeyStore.digital_card_order.detail(variables),
    enabled: !!variables.id && enabled,
    suspense,
  })
}

export const useInfiniteLayouts = (
  variables: Omit<GetLayoutsQueryVariables, 'offset' | 'limit'>,
  { suspense }: { suspense?: boolean } = {},
) => {
  const resultsPerPage = 20
  const { getNextPageParam, getOffset, limit } =
    useOffsetLimitPagination(resultsPerPage)

  return useInfiniteQuery({
    queryKey: queryKeyStore.layout.infinite({
      ...variables,
      offset: 0,
      limit: limit,
    }).queryKey,
    queryFn: args => {
      const { pageParam = 1 } = args
      const offset = getOffset(pageParam)

      return queryKeyStore.layout
        .infinite({
          ...variables,
          offset: Math.round(offset),
          limit,
        })
        .queryFn(args)
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.layouts.length === 0) {
        // We'll stop when we receive an empty page
        return undefined
      } else {
        return getNextPageParam(
          // As we do not have the total count, we'll just stop when we receive an empty page
          // in the condition above. We can just pretend there's an infinite number of layout here.
          { results: lastPage.layouts, count: Infinity },
          allPages.map(page => ({
            results: page.layouts,
            count: page.layouts.length ?? 0,
          })),
        )
      }
    },
    suspense,
    staleTime: 10_000,
  })
}

export const useLayout = (variables?: GetLayoutQueryVariables) => {
  const query = useQuery({
    ...queryKeyStore.layout.detail(variables ?? { id: 'DNE' }), // This should technically never happen with the enabled flag below
    enabled: !!variables?.id,
  })
  return {
    ...query,
    data: query.data?.layout,
  }
}

export const useImages = (
  variables: GetImagesQueryVariables,
  { enabled, suspense }: { enabled?: boolean; suspense?: boolean } = {},
) => {
  return useQuery({
    ...queryKeyStore.image.list(variables),
    enabled,
    suspense,
    staleTime: 10_000,
  })
}

export const useInfiniteImages = () => {
  const { getNextPageParam, getOffset, limit } = useOffsetLimitPagination(20)

  return useInfiniteQuery({
    ...queryKeyStore.image.infinite({ limit: limit }),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length === 0) {
        // We'll stop when we receive an empty page
        return undefined
      } else {
        const pageNumber = getNextPageParam(
          // As we do not have the total count, we'll just stop when we receive an empty page
          // in the condition above. We can just pretend there's an infinite number of layouts here.
          { results: lastPage, count: Infinity },
          allPages.map(page => ({
            results: page,
            count: page.length ?? 0,
          })),
        )
        return pageNumber !== undefined ? getOffset(pageNumber) : undefined
      }
    },
    staleTime: 10_000,
  })
}

export const useCreditsBalance = (isEnabled: boolean = true) => {
  return useQuery({
    ...queryKeyStore.referralCredits.balance,
    enabled: isEnabled,
  })
}

export const useDetailedReferralCredits = (isEnabled: boolean = true) => {
  return useQuery({
    ...queryKeyStore.referralCredits.detailed,
    enabled: isEnabled,
  })
}

export const useOrder = (variables: GetOrderQueryVariables) => {
  const query = useQuery({ ...queryKeyStore.order.detail(variables) })

  return {
    ...query,
    data: query.data?.order,
  }
}

export const useFonts = ({ suspense }: { suspense?: boolean } = {}) => {
  const query = useQuery({
    ...queryKeyStore.font.list,
    suspense,
    staleTime: stableDataStaleTime,
  })
  const signatures: FontFragment[] = []
  const fonts: FontFragment[] = []
  const allFonts = query.data?.fonts

  if (allFonts) {
    for (const font of allFonts) {
      if (font.isSig) {
        // tslint:disable-next-line:no-array-mutation
        signatures.push(font)
      } else {
        // tslint:disable-next-line:no-array-mutation
        fonts?.push(font)
      }
    }
  }

  return {
    ...query,
    data: {
      all: allFonts,
      signatures,
      fonts,
    },
  }
}

export const usePlansDescriptions = ({
  enabled,
}: { enabled?: boolean } = {}) => {
  return useQuery({
    ...queryKeyStore.plansDescriptions.list,
    enabled,
    staleTime: stableDataStaleTime,
  })
}

export const usePlanAddonsDescriptions = ({
  enabled,
}: { enabled?: boolean } = {}) => {
  return useQuery({
    ...queryKeyStore.planAddonsDescriptions.list,
    enabled,
    staleTime: stableDataStaleTime,
  })
}

export const useGetPlanMove = (
  variables: GetPlanMoveQueryVariables,
  shouldNotTrigger?: boolean,
) => {
  const planMoveDetail = queryKeyStore.plan_move.detail(variables)
  return useQuery({
    ...planMoveDetail,
    enabled: !!!shouldNotTrigger,
  })
}

export const usePlans = ({
  suspense,
}: {
  suspense?: boolean
} = {}) =>
  useQuery({
    ...queryKeyStore.plan.list,
    suspense,
    staleTime: stableDataStaleTime,
  })

export const usePricingPage = ({
  enabled,
  suspense,
}: { enabled?: boolean; suspense?: boolean } = {}) =>
  useQuery({
    ...queryKeyStore.pricing_page.list,
    enabled,
    suspense,
    staleTime: stableDataStaleTime,
  })

/**
 * Performs OIDC authentication, first it gets the authenticatioon code from /soc_oauth2/authorize_frontend (instead of the usual flow of directing the user to /authenticate).
 * Once the authentication code is present it passes it to the /token endpoint to retrieve the access_token that can then be used on all Saleor
 * requests. The access token is persisted in local storage, if the token is found the user won't be reauthenticated it will just be returned from LS.
 * If the token is close to expiring (1 hour), a refresh token will be used to obtain a new acccess_token.
 *
 * @returns access token details (type SaleorAuthentication)
 */
export const useSaleorAuth = () => {
  const redirectUri = window.location.href.split('#')[0]

  const account = useSelector(state => state.user.account)

  const persistedSaleorCustomer = getPersistedSaleorAuth()
  const ONE_HOUR = 3_600_000
  const isTokenExpiring =
    persistedSaleorCustomer &&
    Date.now() + ONE_HOUR >= persistedSaleorCustomer.expires_at

  const authorizationCodeGrantMutation = useMutation({
    mutationFn: async () => {
      const codeVerifier = generateCodeVerifier()
      const codeChallenge = base64URLEncode(await sha256(codeVerifier))

      const code = await authorizeSaleorOIDC({
        codeChallenge,
        redirectUri,
      })
      const data = await getSaleorAccessToken(code, codeVerifier, redirectUri)
      if (!data || !data.success) {
        throw new Error('Error getting access token...')
      }
      return data
    },
    retry: 3,
    onSuccess: data => {
      persistSaleorAccessToken(data)
    },
    onError: error => {
      console.error('Error authorizing OIDC:', error)
    },
  })

  const refreshTokenMutation = useMutation({
    mutationFn: async ({ refreshToken }: { refreshToken: string }) => {
      const data = await getSaleorRefreshToken(refreshToken)
      if (!data || !data.success) {
        throw new Error('Error refreshing access token...')
      }
      return data
    },
    retry: 3,
    onSuccess: data => {
      persistSaleorAccessToken(data)
    },
    onError: error => {
      // If token refresh failed, just try to get a new token
      console.error('Error refreshing token:', error)
      authorizationCodeGrantMutation.mutate()
    },
  })

  useEffect(() => {
    if (account?.email && account?.username) {
      if (persistedSaleorCustomer) {
        if (isTokenExpiring) {
          refreshTokenMutation.mutate({
            refreshToken: persistedSaleorCustomer.refresh_token,
          })
        }
      } else {
        authorizationCodeGrantMutation.mutate()
      }
    }
    // We only want to re-run this effect when either:
    // 1. The user changes
    // 2. The token will expire soon
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    account?.email,
    account?.username,
    persistedSaleorCustomer,
    isTokenExpiring,
  ])

  return getPersistedSaleorAuth()
}

export const useCheckout = (variables?: Omit<CheckoutQueryVariables, 'id'>) => {
  const { state } = useContext(CheckoutContext)

  const id = state.checkout ?? ''
  return useQuery({
    ...queryKeyStore.checkout.detail({ ...variables, id }),
    enabled: !!id,
  })
}

export const useCoachingDashboardMarketingContent = () => {
  return useQuery({
    ...queryKeyStore.marketing_content.coaching_dashboard,
    staleTime: stableDataStaleTime,
  })
}

export const useCreditCardManagerMarketingContent = ({
  enabled,
  suspense,
}: { enabled?: boolean; suspense?: boolean } = {}) => {
  return useQuery({
    ...queryKeyStore.marketing_content.credit_card_manager,
    enabled,
    suspense,
    staleTime: stableDataStaleTime,
  })
}

export const useContactRequestMarketingContent = () => {
  return useQuery({
    ...queryKeyStore.marketing_content.contact_request_mkt,
    staleTime: stableDataStaleTime,
  })
}

export const useSubmitRequestedContactMarketingContent = () => {
  return useQuery({
    ...queryKeyStore.marketing_content.submit_requested_contact,
    staleTime: stableDataStaleTime,
  })
}

export const useCreateCheckout = () => {
  const queryClient = useQueryClient()
  const { updateCheckoutVouchers } = useUpdateCheckoutVouchers()

  return useMutation({
    mutationFn: createCheckout,
    onSettled: data => {
      if (data?.errors && data.errors.length) {
        // TODO - How do we want to handle errors?
        throw Error('There was a problem creating the checkout')
      }
      if (data?.checkout) {
        const searchParams = new URLSearchParams(window.location.search)
        searchParams.set('checkout', data.checkout.id)
        searchParams.delete('checkout')
        searchParams.append('checkout', data.checkout.id)
        const newUrl = `${window.location.pathname}?${searchParams.toString()}`

        browserHistory.push(newUrl)

        // Window popstate event doesn't trigger when history state changes so we dispatch a custom event
        // See Checkout context for this event listener
        window.dispatchEvent(new Event('update_checkout'))

        // Manually write to the cache with the mutation response
        queryClient.setQueryData(
          queryKeyStore.checkout.detail({ id: data.checkout.id }).queryKey,
          data.checkout,
        )
        updateCheckoutVouchers(data.checkout)
      }
    },
  })
}

export const useUpdateCheckoutBillingAddress = () => {
  const queryClient = useQueryClient()
  const { updateCheckoutVouchers } = useUpdateCheckoutVouchers()
  const { state } = useContext(CheckoutContext)
  const id = state.checkout ?? ''

  return useMutation({
    mutationFn: (
      variables: Omit<UpdateCheckoutBillingAddressMutationVariables, 'id'>,
    ) => updateCheckoutBillingAddress({ ...variables, id }),
    onSettled: data => {
      if (data?.errors && data.errors.length) {
        // TODO - How do we want to handle errors?
        throw Error('There was a problem updating the checkout')
      }
      if (data?.checkout) {
        // Manually write to the cache with the mutation response
        queryClient.setQueryData(
          queryKeyStore.checkout.detail({ id: data.checkout.id }).queryKey,
          data.checkout,
        )
        updateCheckoutVouchers(data.checkout)
      }
    },
  })
}

export const useAddCheckoutLines = () => {
  const queryClient = useQueryClient()
  const { updateCheckoutVouchers } = useUpdateCheckoutVouchers()

  return useMutation({
    mutationFn: addCheckoutLines,
    onSettled: (data, _err) => {
      if (data?.errors && data.errors.length) {
        // TODO - How do we want to handle errors?
        throw Error('There was a problem adding the line to the checkout')
      }
      if (data?.checkout) {
        queryClient.setQueryData(
          queryKeyStore.checkout.detail({ id: data.checkout.id }).queryKey,
          data.checkout,
        )
        updateCheckoutVouchers(data.checkout)
      }
    },
  })
}

export const useAddCheckoutPromoCode = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: addCheckoutPromoCode,
    onSettled: (data, _err) => {
      if (data?.errors && data.errors.length) {
        // TODO - How do we want to handle errors?
        throw Error('There was a problem adding the promo code to the checkout')
      }
      if (data?.checkout) {
        queryClient.setQueryData(
          queryKeyStore.checkout.detail({ id: data.checkout.id }).queryKey,
          data.checkout,
        )
      }
    },
  })
}

export const useRemoveCheckoutPromoCode = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: removeCheckoutPromoCode,
    onSettled: (data, _err) => {
      if (data?.errors && data.errors.length) {
        // TODO - How do we want to handle errors?
        throw Error(
          'There was a problem removing the promo code from the checkout',
        )
      }
      if (data?.checkout) {
        queryClient.setQueryData(
          queryKeyStore.checkout.detail({ id: data.checkout.id }).queryKey,
          data.checkout,
        )
      }
    },
  })
}

export const useSaleorCustomer = () => {
  return useQuery({
    ...queryKeyStore.saleor_customer.detail,
  })
}

export const useUpdateSaleorCustomer = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateSaleorCustomer,
    onSettled: data => {
      if (data?.errors && data.errors.length) {
        // TODO - How do we want to handle errors?
        throw Error('There was a problem creating the checkout')
      }
      if (data?.user) {
        queryClient.setQueryData(
          queryKeyStore.saleor_customer.detail.queryKey,
          data.user,
        )
      }
    },
  })
}

export const useDeleteCheckoutLines = () => {
  const queryClient = useQueryClient()
  const { state } = useContext(CheckoutContext)
  const { updateCheckoutVouchers } = useUpdateCheckoutVouchers()
  const id = state.checkout ?? ''

  return useMutation({
    mutationFn: (variables: Omit<DeleteCheckoutLinesMutationVariables, 'id'>) =>
      deleteCheckoutLines({ ...variables, id }),
    onMutate: async variables => {
      const queryKey = queryKeyStore.checkout.detail({
        id,
      }).queryKey
      await queryClient.cancelQueries({ queryKey })
      const previousCheckout =
        queryClient.getQueryData<CheckoutFragment>(queryKey)

      if (previousCheckout) {
        queryClient.setQueryData<CheckoutFragment>(queryKey, old => {
          const deleteLines =
            typeof variables.linesIds === 'string'
              ? [variables.linesIds]
              : variables.linesIds

          if (old) {
            return {
              ...old,
              lines: old.lines.filter(
                line => !deleteLines.some(id => id === line.id),
              ),
            }
          }
          return undefined
        })
      }

      return { previousCheckout }
    },
    onSettled: (data, _err) => {
      if (data?.errors && data.errors.length > 0) {
        throw Error('Could not delete checkout line')
      }

      if (data?.checkout) {
        queryClient.setQueryData(
          queryKeyStore.checkout.detail({ id }).queryKey,
          data.checkout,
        )
        updateCheckoutVouchers(data.checkout)
      }
    },
  })
}

export const useProductVariant = (
  variables: ProductVariantQueryVariables,
  isEnabled?: boolean,
) => {
  return useQuery({
    ...queryKeyStore.saleor_product_variant.detail({
      ...variables,
      channel: 'default-channel',
    }),
    enabled:
      !!(variables.id || variables.sku || variables.externalReference) &&
      isEnabled,
  })
}

export const useProductVariants = (
  variables: ProductVariantsQueryVariables,
  isEnabled?: boolean,
) => {
  return useInfiniteQuery({
    queryFn: ({ pageParam }) =>
      getProductVariants({
        first: variables.first,
        after: pageParam,
        ids: variables.ids,
        filter: variables.filter,
      }),
    queryKey:
      queryKeyStore.saleor_infinite_product_variants.list(variables).queryKey,
    getNextPageParam: lastPage => {
      if (lastPage?.pageInfo?.hasNextPage) {
        return lastPage.pageInfo.endCursor
      }
      return undefined
    },
    enabled: isEnabled,
  })
}

export const useDigitalCards = (
  variables: Parameters<typeof queryKeyStore.digitalCard.infinite>[0],
  { enabled }: { enabled?: boolean } = {},
) => {
  return useInfiniteQuery({
    ...queryKeyStore.digitalCard.infinite(variables),
    getNextPageParam: lastPage => {
      if (lastPage?.pageInfo.hasNextPage) {
        return lastPage.pageInfo.endCursor
      }
      return undefined
    },
    enabled,
  })
}

export const useDigitalCardsReceived = (
  variables: Parameters<typeof queryKeyStore.digitalCard.infiniteReceived>[0],
  { enabled }: { enabled?: boolean } = {},
) => {
  return useInfiniteQuery({
    ...queryKeyStore.digitalCard.infiniteReceived(variables),
    getNextPageParam: lastPage => {
      if (lastPage?.pageInfo.hasNextPage) {
        return lastPage.pageInfo.endCursor
      }
      return undefined
    },
    enabled,
  })
}

export const useInitializeTransaction = () => {
  const { state } = useContext(CheckoutContext)
  const id = state.checkout ?? ''

  return useMutation({
    mutationFn: (
      variables: Omit<
        InitializeTransactionMutationVariables,
        'paymentGateway' | 'id'
      > & { stripeCustomerId: string; stripeSourceId?: string },
    ) => {
      const { stripeCustomerId, stripeSourceId, ...rawVariables } = variables

      return createTransaction({
        ...rawVariables,
        id: id ?? '',
        paymentGateway: {
          id: 'app.saleor.stripe',
          data: {
            customer: stripeCustomerId,
            payment_method: stripeSourceId,
          },
        }, // We only have 1 payment gateway so this is hard coded for now instead of fetching uneccessarily
      })
    },
  })
}

export const useCheckoutPayment = () => {
  const { state } = useContext(CheckoutContext)
  const id = state.checkout ?? ''

  return useMutation({
    mutationFn: () =>
      createCheckoutPayment({
        id,
        input: { gateway: 'saleor.payments.stripe' },
      }),
  })
}

export const useCompleteCheckout = () => {
  const { state } = useContext(CheckoutContext)
  const id = state.checkout ?? ''
  const account = useSelector(state => state.user.account)

  return useMutation({
    mutationFn: (variables: Omit<CompleteCheckoutMutationVariables, 'id'>) =>
      completeCheckout({
        ...variables,
        id,
        paymentData: JSON.stringify({
          payment_method_id: account?.stripeSource?.id,
          off_session: false,
        }),
      }),
    retry: 3,
    retryDelay: attempt => attempt * 1000, // Retry creating the order 3 times (1000ms delay between)
  })
}

export const useSaleorOrder = (variables: SaleorOrderQueryVariables) => {
  return useQuery(queryKeyStore.saleor_order.detail(variables))
}

export const useSponsor = (
  variables: GetSponsorQueryVariables,
  isEnabled: boolean = true,
) => {
  return useQuery({
    ...queryKeyStore.sponsor.detail(variables),
    enabled: isEnabled,
  })
}

export const usePossibleSponsors = ({
  enabled,
  suspense,
}: { enabled?: boolean; suspense?: boolean } = {}) => {
  return useQuery({
    ...queryKeyStore.possible_sponsors.list,
    enabled,
    suspense,
  })
}

export const useUpdateCheckoutEmail = () => {
  const queryClient = useQueryClient()
  const { state } = useContext(CheckoutContext)
  const id = state.checkout ?? ''

  return useMutation({
    mutationFn: (variables: Omit<UpdateCheckoutEmailMutationVariables, 'id'>) =>
      updateCheckoutEmail({ ...variables, id }),
    onSettled: (data, _err) => {
      if (data?.errors && data.errors.length > 0) {
        throw Error('Could not update checkout email')
      }

      if (data?.checkout) {
        queryClient.setQueryData(
          queryKeyStore.checkout.detail({ id }).queryKey,
          data.checkout,
        )
      }
    },
  })
}

export const useCustomerAttach = () => {
  const queryClient = useQueryClient()
  const { state } = useContext(CheckoutContext)
  const id = state.checkout ?? ''

  return useMutation({
    mutationFn: (
      variables: Omit<AttachCustomerMutationVariables, 'id'> & {
        token?: string
      },
    ) => attachCustomerToCheckout({ ...variables, id }, variables.token),
    onSettled: (data, _err) => {
      if (data?.errors && data.errors.length > 0) {
        throw Error('Could not attach customer to order')
      }

      if (data?.checkout) {
        queryClient.setQueryData(
          queryKeyStore.checkout.detail({ id }).queryKey,
          data.checkout,
        )
      }
    },
  })
}

export const useCampaignStoreSubcategories = (paginationOpts: {
  resultsPerPage: number
}) => {
  return useInfiniteQuery({
    keepPreviousData: true,
    queryKey: queryKeyStore.infinite_campaign_store_subcategories.list({
      subcategoriesFirst: paginationOpts.resultsPerPage,
    }).queryKey,
    queryFn: args => {
      const pageParam = {
        direction: args.pageParam?.direction ?? 'forward',
        cursor: args.pageParam?.cursor,
      }
      const connection = connectionFactory(
        pageParam.direction as 'forward' | 'backward',
        paginationOpts.resultsPerPage,
        pageParam.cursor,
      )
      return queryKeyStore.infinite_campaign_store_subcategories
        .list({
          subcategoriesLast: connection.last,
          subcategoriesBefore: connection.before,
          subcategoriesAfter: connection.after,
          subcategoriesFirst: connection.first ?? paginationOpts.resultsPerPage,
        })
        .queryFn(args)
    },
    getNextPageParam: (
      lastPage,
    ): { cursor: string; direction: 'forward' | 'backward' } | undefined => {
      if (lastPage?.pageInfo.hasNextPage && lastPage.pageInfo.endCursor) {
        return { cursor: lastPage.pageInfo.endCursor, direction: 'forward' }
      }

      return undefined
    },
    getPreviousPageParam: (
      nextPage,
    ): { cursor: string; direction: 'forward' | 'backward' } | undefined => {
      if (nextPage?.pageInfo.hasNextPage && nextPage.pageInfo.startCursor) {
        return { cursor: nextPage.pageInfo.startCursor, direction: 'backward' }
      }
      return undefined
    },
  })
}

export const useCampaignStoreSubcategoryProducts = (
  variables: CampaignStoreSubcategoryQueryVariables,
  isEnabled?: boolean,
  resultsPer: number = 10,
) => {
  // const queryClient = useQueryClient()
  return useInfiniteQuery({
    enabled: !!isEnabled,
    queryKey: queryKeyStore.infinite_campaign_store_subcategory_products.list({
      productsFirst: resultsPer ?? variables.productsFirst,
      ...variables,
    }).queryKey,
    queryFn: args => {
      const pageParam = {
        direction: args.pageParam?.direction ?? 'forward',
        cursor:
          args.pageParam?.cursor ??
          (variables.productsAfter || variables.productsBefore),
      }
      const connection = connectionFactory(
        pageParam.direction as 'forward' | 'backward',
        resultsPer,
        pageParam.cursor,
      )
      return queryKeyStore.infinite_campaign_store_subcategory_products
        .list({
          id: variables.id,
          productsBefore: connection.before,
          productsAfter: connection.after,
          productsFirst: connection.first ?? resultsPer,
          productsLast: connection.last,
        })
        .queryFn(args)
    },
    getNextPageParam: (
      lastPage,
    ): { cursor: string; direction: 'forward' | 'backward' } | undefined => {
      if (lastPage?.pageInfo.hasNextPage && lastPage.pageInfo.endCursor) {
        return { cursor: lastPage.pageInfo.endCursor, direction: 'forward' }
      }

      return undefined
    },
    getPreviousPageParam: (
      nextPage,
    ): { cursor: string; direction: 'forward' | 'backward' } | undefined => {
      if (nextPage?.pageInfo.hasNextPage && nextPage.pageInfo.startCursor) {
        return { cursor: nextPage.pageInfo.startCursor, direction: 'backward' }
      }
      return undefined
    },
  })
}

export const useUpdateCheckoutLineQuantity = () => {
  const queryClient = useQueryClient()
  const { updateCheckoutVouchers } = useUpdateCheckoutVouchers()
  const { state } = useContext(CheckoutContext)
  const id = state.checkout ?? ''
  const queryKey = queryKeyStore.checkout.detail({
    id,
  }).queryKey
  const mutation = useMutation({
    mutationFn: async (
      variables: Omit<UpdateCheckoutLineQuantityMutationVariables, 'id'>,
    ) => {
      return updateCheckoutLineQuantity({ ...variables, id })
    },
    onMutate: async variables => {
      await queryClient.cancelQueries({ queryKey })
      const previousCheckout =
        queryClient.getQueryData<CheckoutFragment>(queryKey)

      if (previousCheckout) {
        queryClient.setQueryData<CheckoutFragment>(queryKey, old => {
          const lines = Array.isArray(variables.lines)
            ? variables.lines
            : [variables.lines]

          if (old) {
            return {
              ...old,
              lines: old.lines.map(line => {
                const match = lines.find(l => l.lineId === line.id)
                if (match) {
                  return {
                    ...line,
                    quantity: match.quantity ?? line.quantity,
                  }
                }
                return line
              }),
            }
          }
          return undefined
        })
      }

      return { previousCheckout }
    },

    onSettled: async (data, error, _variables, context) => {
      const errorRes = data?.errors
      const queryKey = queryKeyStore.lines.list({
        checkoutId: id,
      }).queryKey

      if ((errorRes && errorRes.length > 0) || error) {
        queryClient.setQueryData(queryKey, context?.previousCheckout)
      }

      if (data?.checkout) {
        queryClient.setQueryData(
          queryKeyStore.checkout.detail({ id: data.checkout.id }).queryKey,
          data.checkout,
        )
        updateCheckoutVouchers(data.checkout)
      }
    },
  })
  return mutation
}

export const useUserCardTokens = () => {
  const res = useQuery({
    ...queryKeyStore.user_card_tokens.detail,
  })

  return res
}

export const useStickerCategories = () => {
  const { getNextPageParam, getOffset, limit } = useOffsetLimitPagination(100)

  return useInfiniteQuery({
    queryKey: queryKeyStore.sticker_category.infinite({
      offset: 0,
      limit: 1,
    }).queryKey,
    queryFn: args => {
      const { pageParam = 1 } = args
      const offset = getOffset(pageParam)

      return queryKeyStore.sticker_category
        .infinite({
          offset,
          limit,
        })
        .queryFn(args)
    },
    getNextPageParam: (lastPage, allPages) =>
      getNextPageParam(
        { ...lastPage, count: lastPage.totalCount ?? 0 },
        allPages.map(page => ({ ...page, count: page.totalCount ?? 0 })),
      ),
  })
}

export const useStickers = (filters?: StickerFilters) => {
  const { getNextPageParam, getOffset, limit } = useOffsetLimitPagination(20)

  return useInfiniteQuery({
    queryKey: queryKeyStore.infinite_stickers.list({
      offset: 0,
      limit: 1,
      filters,
    }).queryKey,
    queryFn: args => {
      const { pageParam = 1 } = args
      const offset = getOffset(pageParam)

      return queryKeyStore.infinite_stickers
        .list({
          offset,
          limit,
          filters,
        })
        .queryFn(args)
    },
    getNextPageParam: (lastPage, allPages) =>
      getNextPageParam(
        { ...lastPage, count: lastPage.totalCount ?? 0 },
        allPages.map(page => ({ ...page, count: page.totalCount ?? 0 })),
      ),
  })
}

export const useGetSubscriptions = () =>
  useQuery({ ...queryKeyStore.subscription.list })

export const useUsers = (
  variables: GetUsersQueryVariables,
  isEnabled?: boolean,
) => useQuery({ ...queryKeyStore.user.list(variables), enabled: isEnabled })

export const useContact = (
  variables: graphql.GetContactQueryVariables,
  { enabled, suspense }: { enabled?: boolean; suspense?: boolean } = {},
) => useQuery({ ...queryKeyStore.contact.detail(variables), enabled, suspense })

export const useContacts = (
  variables: graphql.GetContactsQueryVariables,
  { enabled, suspense }: { enabled?: boolean; suspense?: boolean } = {},
) => useQuery({ ...queryKeyStore.contact.list(variables), enabled, suspense })

export const useMinimalContacts = (
  variables: graphql.GetMinimalContactsQueryVariables,
  { enabled, suspense }: { enabled?: boolean; suspense?: boolean } = {},
) =>
  useQuery({
    ...queryKeyStore.contact.listMinimal(variables),
    enabled,
    suspense,
  })

export const useContactIds = (
  variables: graphql.GetContactIdsQueryVariables,
  { enabled, suspense }: { enabled?: boolean; suspense?: boolean } = {},
) =>
  useQuery({ ...queryKeyStore.contact.listIds(variables), enabled, suspense })

export const usePaginatedContacts = (
  variables: Omit<
    graphql.GetPaginatedContactsQueryVariables,
    'offset' | 'limit'
  > & {
    page: number
    resultsPerPage?: number
  },
  { enabled, suspense }: { enabled?: boolean; suspense?: boolean } = {},
) => {
  const { page, ...vars } = variables
  const resultsPerPage = vars.resultsPerPage || 20
  const { getOffset, limit } = useOffsetLimitPagination(resultsPerPage)
  const offset = getOffset(page)
  return useQuery({
    ...queryKeyStore.contact.listPaginated({ offset, limit, ...vars }),
    enabled,
    suspense,
  })
}

export const useInfiniteMinimalContacts = (
  variables: Parameters<typeof queryKeyStore.contact.infiniteMinimal>[0],
  { enabled }: { enabled?: boolean } = {},
) => {
  return useInfiniteQuery({
    ...queryKeyStore.contact.infiniteMinimal(variables),
    getNextPageParam: (lastPage, allPages) => {
      // In react query v5 this function will also receive lastPageParam,
      // which will make this calculation a lot simpler.
      const newOffset = allPages.reduce(
        (totalLength, page) => totalLength + page.contacts.length,
        0,
      )
      if (newOffset < lastPage.count) {
        return newOffset
      }
      return undefined
    },
    enabled,
  })
}

export const useContactChildren = (
  variables: graphql.GetContactChildrenQueryVariables,
  { enabled, suspense }: { enabled?: boolean; suspense?: boolean } = {},
) =>
  useQuery({
    ...queryKeyStore.contact.listChildren(variables),
    enabled,
    suspense,
  })

export const useGroup = (
  variables: graphql.GetGroupQueryVariables,
  { enabled, suspense }: { enabled?: boolean; suspense?: boolean } = {},
) =>
  useQuery({
    ...queryKeyStore.group.detailed(variables),
    enabled: !!variables.id && enabled,
    suspense,
  })

export const usePaginatedGroups = (
  variables: Omit<
    graphql.GetPaginatedGroupsQueryVariables,
    'offset' | 'limit'
  > & {
    page: number
    resultsPerPage?: number
  },
  { enabled, suspense }: { enabled?: boolean; suspense?: boolean } = {},
) => {
  const { page, ...vars } = variables
  const resultsPerPage = vars.resultsPerPage || 20
  const { getOffset, limit } = useOffsetLimitPagination(resultsPerPage)
  const offset = getOffset(page)
  return useQuery({
    ...queryKeyStore.group.listPaginated({ offset, limit, ...vars }),
    enabled,
    suspense,
  })
}

export const useAllGroups = ({
  enabled,
  suspense,
}: { enabled?: boolean; suspense?: boolean } = {}) => {
  return useQuery({
    ...queryKeyStore.group.listAll,
    enabled,
    suspense,
  })
}

export const usePaginatedContactRequests = (
  variables: Omit<
    graphql.GetPaginatedContactRequestsQueryVariables,
    'offset' | 'limit'
  > & {
    page: number
  },
  { enabled, suspense }: { enabled?: boolean; suspense?: boolean } = {},
) => {
  const { page, ...vars } = variables
  const resultsPerPage = 20
  const { getOffset, limit } = useOffsetLimitPagination(resultsPerPage)
  const offset = getOffset(page)
  return useQuery({
    ...queryKeyStore.contact_requests.list_paginated({
      offset,
      limit,
      ...vars,
    }),
    enabled,
    suspense,
  })
}

export const useDefaultSubscriptionIds = ({
  suspense,
}: {
  suspense?: boolean
}) => {
  return useQuery({ ...queryKeyStore.defaultSubscriptionIds.detail, suspense })
}

export const useExclusiveOffer = ({
  enabled,
  suspense,
}: { enabled?: boolean; suspense?: boolean } = {}) => {
  const query = useQuery({
    ...queryKeyStore.exclusive_offer.detail,
    enabled,
    suspense,
  })
  return query
}

export const useInfiniteDigitalCardProducts = (
  variables: graphql.GetPaginatedDigitalGiftCardProductsQueryVariables,
  isEnabled?: boolean,
) => {
  return useInfiniteQuery({
    ...queryKeyStore.digital_gift_card_product.listPaginated(variables),
    getNextPageParam: lastPage => {
      if (lastPage?.results.length === lastPage.pagination.page.limit) {
        return lastPage.pagination.cursors.after
      }
      return undefined
    },
    enabled: isEnabled,
  })
}

export const useDigitalGiftCardsAvailableCountries = ({
  enabled,
  suspense,
}: { enabled?: boolean; suspense?: boolean } = {}) => {
  return useQuery({
    ...queryKeyStore.digital_gift_cards_available_countries.list,
    enabled,
    suspense,
  })
}

export const useGlobalSettings = ({
  enabled,
  suspense,
}: { enabled?: boolean; suspense?: boolean } = {}) => {
  return useQuery({
    ...queryKeyStore.global_settings.detail,
    enabled,
    suspense,
  })
}

export const useDigitalGiftCardProduct = (
  variables: graphql.GetDigitalGiftCardProductQueryVariables,
  isEnabled?: boolean,
) => {
  return useQuery({
    ...queryKeyStore.digital_gift_card_product.detail(variables),
    enabled: variables.code !== '' && isEnabled,
    staleTime: stableDataStaleTime,
  })
}

export const useDigitalGiftCardProducts = (productCodes: string[]) => {
  return useQueries({
    queries: productCodes.map(code => ({
      ...queryKeyStore.digital_gift_card_product.detail({
        code,
      }),
      retry: false,
      staleTime: stableDataStaleTime,
    })),
  })
}

export const useInfiniteGifts = (
  variables: Omit<graphql.GetGiftsQueryVariables, 'offset' | 'limit'>,
  { enabled }: { enabled?: boolean } = {},
) => {
  const { getNextPageParam, getOffset, limit } = useOffsetLimitPagination(20)

  return useInfiniteQuery({
    ...queryKeyStore.gift.infinite({ ...variables, limit }),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length === 0) {
        // Stop when we receive an empty page
        return undefined
      } else {
        const pageNumber = getNextPageParam(
          // Pretend infinite count since we don't have total
          { results: lastPage, count: Infinity },
          allPages.map(page => ({
            results: page,
            count: page.length ?? 0,
          })),
        )
        return pageNumber !== undefined ? getOffset(pageNumber) : undefined
      }
    },
    enabled,
  })
}

export const useUserApplicationSettings = <T extends AppLabel>(
  variables: graphql.GetUserApplicationSettingsQueryVariables & { appLabel: T },
) => {
  return useQuery({
    ...queryKeyStore.user_application_settings.detail(variables),
    staleTime: stableDataStaleTime,
  })
}

export const useAffiliateBonusInfo = ({
  enabled,
  suspense,
}: { enabled?: boolean; suspense?: boolean } = {}) => {
  return useQuery({
    ...queryKeyStore.affiliate_bonus_info.detail,
    enabled,
    suspense,
  })
}

export const useBasicPostageCosts = ({
  enabled,
  suspense,
}: { enabled?: boolean; suspense?: boolean } = {}) => {
  return useQuery({
    ...queryKeyStore.basic_postage_costs.detail,
    enabled,
    suspense,
  })
}

export const useDoubleBonusReports = ({
  enabled,
  suspense,
}: { enabled?: boolean; suspense?: boolean } = {}) => {
  return useQuery({
    ...queryKeyStore.double_bonus_reports.list,
    enabled,
    suspense,
  })
}

export const useGiftCategories = ({
  enabled,
  suspense,
}: { enabled?: boolean; suspense?: boolean } = {}) => {
  return useQuery({
    ...queryKeyStore.giftCategories.list,
    staleTime: stableDataStaleTime,
    enabled,
    suspense,
  })
}

export const useGift = (
  variables: { id?: string; slug?: string },
  { enabled, suspense }: { enabled?: boolean; suspense?: boolean } = {},
) => {
  return useQuery({
    ...queryKeyStore.gifts.detail(variables),
    staleTime: stableDataStaleTime,
    enabled,
    suspense,
  })
}

export const useGifts = (
  variables: graphql.GetGiftsQueryVariables,
  { enabled, suspense }: { enabled?: boolean; suspense?: boolean } = {},
) => {
  return useQuery({
    ...queryKeyStore.gifts.list(variables),
    staleTime: stableDataStaleTime,
    enabled,
    suspense,
  })
}

export const useGetCampaignUnavailableGifts = (
  variables: graphql.GetCampaignUnavailableGiftsQueryVariables,
  { enabled, suspense }: { enabled?: boolean; suspense?: boolean } = {},
) => {
  return useQuery({
    ...queryKeyStore.campaign.unavailableGifts(variables),
    staleTime: stableDataStaleTime,
    enabled,
    suspense,
  })
}
